import React from "react";
import { Link } from "react-router-dom";
import {
  Briefcase,
  Users,
  Filter,
  CalendarCheck,
  MessageSquare,
  BarChart3,
  ArrowRight,
  UserPlus,
  Search,
  CheckCircle,
  FileSearch,
  Mail
} from "lucide-react";

export default function RecruitmentPipeline() {
  const poppinsStyle = { fontFamily: "'Poppins', sans-serif" };
  const robotoStyle = { fontFamily: "'Roboto', sans-serif" };

  return (
    <div className="min-h-screen bg-white" style={robotoStyle}>
      {/* Hero Section */}
      <div className="bg-white py-16 px-6 border-b border-slate-50">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-[11px] font-bold uppercase tracking-wider mb-6" style={poppinsStyle}>
            <UserPlus size={14} />
            Advanced Talent Acquisition
          </div>
          
          <h1 className="text-3xl md:text-4xl font-medium text-gray-900 mb-6 leading-tight" style={poppinsStyle}>
            Smart ATS & <br />
            Collaborative Recruitment Pipeline
          </h1>
          
          <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Move beyond messy spreadsheets. Streamline your entire hiring process from 
            Job Requisition to E-Offer letters with a centralized, data-driven 
            Applicant Tracking System.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-24">
        
        {/* NEW: Visual Candidate Pipeline (Replaces Placeholder) */}
        <div className="mt-12 mb-20">
          <h3 className="text-center text-sm font-bold text-slate-400 uppercase tracking-widest mb-10" style={poppinsStyle}>
            Standardized Hiring Workflow
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <PipelineStep icon={<Search size={18}/>} label="Sourcing" color="bg-slate-100 text-slate-600" />
            <PipelineStep icon={<FileSearch size={18}/>} label="Screening" color="bg-blue-50 text-blue-600" />
            <PipelineStep icon={<CalendarCheck size={18}/>} label="Interview" color="bg-indigo-50 text-indigo-600" />
            <PipelineStep icon={<CheckCircle size={18}/>} label="Evaluation" color="bg-purple-50 text-purple-600" />
            <PipelineStep icon={<Mail size={18}/>} label="Hired" color="bg-emerald-50 text-emerald-600" />
          </div>
        </div>

        {/* Feature Rows (Refined for Enterprise) */}
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-12 mb-20">
          <FeatureRow 
            icon={<Briefcase />} 
            title="Strategic Requisitions"
            desc="Structured approval workflows for new openings, ensuring headcount budget compliance."
            poppins={poppinsStyle}
          />
          <FeatureRow 
            icon={<Filter />} 
            title="Automated Parsing"
            desc="AI-powered resume parsing that extracts skills and experience to rank top talent instantly."
            poppins={poppinsStyle}
          />
          <FeatureRow 
            icon={<Users />} 
            title="Candidate 360"
            desc="A complete history of every applicant, including previous applications, notes, and scores."
            poppins={poppinsStyle}
          />
          <FeatureRow 
            icon={<CalendarCheck />} 
            title="Calendar Sync"
            desc="Native integration with Google/Outlook for conflict-free interview scheduling."
            poppins={poppinsStyle}
          />
          <FeatureRow 
            icon={<MessageSquare />} 
            title="Team Scorecards"
            desc="Empower hiring managers with custom interview rubrics for unbiased, data-backed decisions."
            poppins={poppinsStyle}
          />
          <FeatureRow 
            icon={<BarChart3 />} 
            title="Sourcing Analytics"
            desc="Identify which job boards and referral channels provide your highest-quality hires."
            poppins={poppinsStyle}
          />
        </div>

        {/* Deep Content: Benefits & Value */}
        <div className="grid md:grid-cols-3 gap-8 py-16 border-t border-slate-100">
           <div className="md:col-span-1">
              <h2 className="text-2xl font-medium text-gray-900 leading-tight" style={poppinsStyle}>
                Why Modern HR Teams Choose Us
              </h2>
           </div>
           <div className="md:col-span-2 grid md:grid-cols-2 gap-8">
              <BenefitCard 
                title="Time-to-Hire" 
                desc="Reduce the hiring cycle by 40% through automated communication and stage tracking."
              />
              <BenefitCard 
                title="Collaborative Hiring" 
                desc="Keep HR and Department Heads on the same page with real-time feedback loops."
              />
              <BenefitCard 
                title="Better Candidate CX" 
                desc="Ensure no candidate is left in the dark with automated status updates and email templates."
              />
              <BenefitCard 
                title="Data Compliance" 
                desc="Maintain GDPR/SOC2 compliance with secure data handling and automated data deletion."
              />
           </div>
        </div>

        {/* Professional CTA */}
        <div className="text-center pt-24 border-t border-slate-100">
          <h3 className="text-xl font-medium text-gray-900 mb-2" style={poppinsStyle}>Build your dream team today</h3>
          <p className="text-sm text-gray-500 mb-10">Stop searching and start hiring with the most intuitive ATS in the market.</p>
          
          <div className="flex justify-center">
            <Link 
              to="/login"
              className="group flex items-center gap-4 bg-gray-900 hover:bg-blue-600 text-white px-8 py-3 rounded-full transition-all duration-300 shadow-lg"
            >
              <div className="text-left">
                <p className="text-[9px] uppercase tracking-widest opacity-70 leading-none mb-1" style={poppinsStyle}>Recruitment Portal</p>
                <p className="text-base font-semibold leading-none" style={poppinsStyle}>Get Started Now</p>
              </div>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Internal Components */
function PipelineStep({ icon, label, color }) {
  return (
    <div className={`flex flex-col items-center p-4 rounded-2xl border border-slate-100 shadow-sm ${color}`}>
      <div className="mb-2">{icon}</div>
      <span className="text-[11px] font-bold uppercase tracking-wider">{label}</span>
    </div>
  );
}

function FeatureRow({ icon, title, desc, poppins }) {
  return (
    <div className="flex gap-5">
      <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-xl bg-slate-50 text-blue-600 border border-slate-100">
        {icon}
      </div>
      <div>
        <h4 className="text-base font-medium text-gray-900 mb-1" style={poppins}>{title}</h4>
        <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function BenefitCard({ title, desc }) {
  return (
    <div className="space-y-2">
      <h5 className="font-bold text-gray-800 text-xs uppercase tracking-widest leading-none border-l-2 border-blue-500 pl-3">
        {title}
      </h5>
      <p className="text-sm text-gray-600 leading-relaxed pl-3">{desc}</p>
    </div>
  );
}